import React from 'react';
import { Link, browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import Tooltip from '~/components/Tooltip';
import { SOURCECLEAR_COMPONENTS, veracodeLogout } from '~/constants/ModelConstants';
import veracodeLogo from '~/images/sca-veracode-logo-white.png';
import AuthService from '~/utils/AuthService';
import NavigationService from '~/utils/NavigationService';
import Helpers from '~/utils/Helpers';
import * as searchActions from '~/search2/actions';
import * as navActions from '~/sidebarNav/actions';
import * as meActions from '~/actions/me';
import * as teamActions from '~/actions/team';
import config from '~/config';
type LoggedInHeaderProps = {
  navActions: object;
  teamActions: object;
  meActions: object;
  searchActions: object;
  myState: object;
  navigationState: object;
  location: object;
  vcPageState: object;
};
class LoggedInHeader extends React.Component<LoggedInHeaderProps, {}> {
  componentDidMount() {
    document.body.addEventListener('click', this.closeSettingsDropdown);
  }
  componentWillUnmount() {
    document.body.removeEventListener('click', this.closeSettingsDropdown);
  }
  focusSearchBar = () => {
    this.catalogSearch.focus();
    this.expandSearchBar();
  };
  expandSearchBar = () => {
    this.props.navActions.expandSearchBar(true);
  };
  collapseSearchBar = () => {
    const self = this;
    setTimeout(function() {
      self.props.navActions.expandSearchBar(false);
    }, 200);
  };
  closeSettingsDropdown = e => {
    if (
      e.target.id !== 'settingsDropdown' &&
      e.target.parentElement.id !== 'settingsDropdown' &&
      this.props.navigationState.settingsDropdownExpanded
    ) {
      this.props.navActions.toggleSettingsDropdown();
    }
  };
  toggleSettingsDropdown = () => {
    this.props.navActions.toggleSettingsDropdown();
  };
  postLogout = () => {
    NavigationService.removeLoginRedirectCookies();
    this.toggleSettingsDropdown();
    browserHistory.push(`/${SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}/search`);
  };
  logout = () => {
    const {
      meActions,
      teamActions,
      vcPageState: { shouldShowVeracodePage },
    } = this.props;
    meActions.clearMe();
    teamActions.updateTeams([]);
    AuthService.logout().then(() => {
      if (shouldShowVeracodePage) {
        window.location = `${config.VERACODE_LOGIN_HOST}${veracodeLogout}`;
      }
      // as long as we're using window.location instead of browserHistory, no need to clear these
      // they'll start from scratch when the browser reloads anyways.
      // we need the me object when deciding where to send the user on logout
      // which is a temporary hacky feature flagging thing anyways
      // meActions.clearMe();
      // teamActions.updateTeams([]);
      this.postLogout();
    });
    if (window.snowplow) {
      window.snowplow('setUserId', null);
    }
  };
  onSearchKeyPress = e => {
    if (e.which === 13) {
      const query = document.getElementById('search-input-nav').value;
      if (
        this.props.location.pathname.indexOf(
          `/${SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}/search`
        ) === -1
      ) {
        browserHistory.push(
          `/${SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}/search?query=${encodeURIComponent(
            query
          )}`
        );
      } else {
        this.props.searchActions.updateFilters({
          query: query,
        });
      }
      document.getElementById('search-input-nav').value = ''; // clear out so it does not get stale.
    }
  };
  render() {
    const {
      myState,
      navigationState,
      vcPageState: { shouldShowVeracodePage },
    } = this.props;
    const { searchBarExpand, settingsDropdownExpanded } = navigationState;
    const { me = {} } = myState;
    const nameToDisplay =
      me.firstName && me.lastName ? me.firstName + ' ' + me.lastName : 'My Account';
    const hasMeEula = !!me.eula && !!me.eula.acceptedVersion;
    const acceptedEula = !!me.eulaAcceptanceDate || hasMeEula;
    const { organization = {} } = me;
    const { admin: isOrgAdmin = false, id: orgName = '' } = organization || {};
    const platformUrl = shouldShowVeracodePage
      ? config.VC_APP_URL
      : `https://${orgName}.${config.PLATFORM_FRONTEND_DOMAIN[0]}`;
    const contactSupportIconClasses = classNames(
      'sci sci--fw sci__chat--help',
      {
        'font--16': shouldShowVeracodePage,
        'font--h6': !shouldShowVeracodePage,
      },
      'p0'
    );
    const documentationIconClasses = classNames(
      'sci sci--fw sci__catalog',
      {
        'font--16': shouldShowVeracodePage,
        'font--h6': !shouldShowVeracodePage,
      },
      'p0'
    );
    return (
      <section className="container--full bg-color--black-dark height--68">
        <section className="container h--100">
          <div className="grid grid--middle grid--full h--100">
            <div className="grid__item pr- col-1-6 flex flex--align-items--center position--relative h--100">
              <Link
                className="font--h3 flex flex--align-items--center link--plain"
                to={`/${SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}/search`}
              >
                {!shouldShowVeracodePage ? (
                  <i className="sci sci__sourceclear-logo--primary color--white sci--sm" />
                ) : (
                  <img src={veracodeLogo} className="vc-logo" />
                )}
              </Link>
              <div
                className={
                  'h--60 top--13 w--1 bg-color--muted right--0 position--absolute' +
                  (searchBarExpand ? ' invisible' : '')
                }
              />
            </div>
            <div className="grid__item flex flex--justify-content--space-between flex--align-items--center">
              <div className="col-1-5 top--nav">
                <input
                  type="text"
                  id="search-input-nav"
                  ref={c => (this.catalogSearch = c)}
                  className="bg-color--white col-1-1 h--auto color--black nav--search__input"
                  placeholder="Libraries & Vulnerabilities"
                  onBlur={this.collapseSearchBar}
                  onFocus={this.expandSearchBar}
                  onKeyPress={this.onSearchKeyPress}
                />
                <label className="nav--search__label" onClick={this.focusSearchBar}>
                  <i className="sci sci__search" />
                </label>
              </div>

              <div className="font--h4 col-5-5 flex flex--align-items--center flex--justify-content--end top--nav">
                <li
                  className="color--white navbar__item"
                  data-automation-id="LoggedInHeader-HomeLink"
                >
                  <a
                    href={
                      shouldShowVeracodePage
                        ? `${config.VC_INTEGRATED_URL}/auth/index.jsp#ScaHomeEUI`
                        : `${platformUrl}/products/software-composition-analysis`
                    }
                    target="_blank"
                    className="btn font--h6 ph-"
                  >
                    Home
                  </a>
                </li>
                <li
                  className="color--white navbar__item"
                  data-automation-id="LoggedInHeader-VulnerabilityDatabaseLink"
                >
                  <Link
                    className="btn font--h6 ph- mr-- selected"
                    to={`/${SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}/search`}
                  >
                    {SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.name}
                  </Link>
                </li>
                <div className="bo-l--1 border-color--muted height--20" />
                <ul className="inline-block m0 pl0">
                  <li className="color--white navbar__item inline">
                    <a
                      href="mailto:support@veracode.com?subject=Request For SourceClear Support"
                      className="btn ph-- text--center"
                    >
                      <Tooltip id={`contact-support`} content="Contact Support">
                        <i className={contactSupportIconClasses} alt="Contact Support" />
                      </Tooltip>
                    </a>
                  </li>
                  <li className="color--white navbar__item inline">
                    <a href={`${config.HELP_URL}`} target="_blank" className="btn ph- text--center">
                      <Tooltip id={`documentation`} content="Documentation">
                        <i className={documentationIconClasses} alt="Documentation" />
                      </Tooltip>
                    </a>
                  </li>
                  {isOrgAdmin && (
                    <li className="color--white navbar__item inline">
                      <a
                        href={`${platformUrl}/org/settings`}
                        target="_blank"
                        className="btn ph- text--center"
                      >
                        <Tooltip id={`org-settings`} content="Organization Settings">
                          <i className="fa fa-gear font--h6" alt="Organization Settings" />
                        </Tooltip>
                      </a>
                    </li>
                  )}
                </ul>
                <div className="bo-l--1 border-color--muted height--20" />
                <div className="navbar__item">
                  <div
                    id="settingsDropdown"
                    className="position--relative pl- pv- font--h7 color--white cursor--pointer header-dropdown--light header-dropdown--light-hover"
                    onClick={this.toggleSettingsDropdown}
                  >
                    <span className="font--h6 text--ellipsis">
                      {Helpers.cut(nameToDisplay, 20)}
                    </span>
                    <i className="sci sci__caret--down pl-" />
                    <div
                      className={
                        'list--group position--absolute right--0 width--200 zIndex-1--screenForeground' +
                        (settingsDropdownExpanded ? ' is-showing--100' : ' is-hiding')
                      }
                    >
                      <div className="list--group__item bg-color--white p0 color--black">
                        {acceptedEula && (
                          <a
                            href={`${platformUrl}/settings/profile`}
                            target="_blank"
                            className="link--no-underline grid grid--full grid--middle p-"
                            onClick={this.toggleSettingsDropdown}
                          >
                            <div className="grid__item col-1-6">
                              <i className="sci sci__settings" />
                            </div>
                            <div className="grid__item">Personal settings</div>
                          </a>
                        )}
                        {!acceptedEula && (
                          <div className="link--no-underline grid grid--full grid--middle p- cursor--not-allowed">
                            <div className="grid__item col-1-6">
                              <i className="sci sci__settings" />
                            </div>
                            <div className="grid__item">Personal settings</div>
                          </div>
                        )}
                      </div>
                      <div className="list--group__item bg-color--white p0 color--black cursor--pointer">
                        <div className="grid grid--full grid--middle p-" onClick={this.logout}>
                          <div className="grid__item col-1-6">
                            <i className="sci sci__data--export" />
                          </div>
                          <div className="grid__item">Logout</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </section>
    );
  }
}

function mapStateToProps(state) {
  return {
    myState: state.myState,
    navigationState: state.navigationState,
    vcPageState: state.vcPageState,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    navActions: bindActionCreators(navActions, dispatch),
    teamActions: bindActionCreators(teamActions, dispatch),
    meActions: bindActionCreators(meActions, dispatch),
    searchActions: bindActionCreators(searchActions, dispatch),
  };
}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LoggedInHeader);
